<template>
  <div class="kit-material-workbench kit-material-workbench-root">
    <div class="app-shell">
      <!-- 顶层维度导航栏（对齐原版设计系统） -->
      <header class="query-band" aria-label="齐套分析维度导航">
        <nav class="dimension-nav" aria-label="齐套分析维度">
          <el-tabs v-model="activeTab" class="dimension-tabs" data-ui="tabs" @tab-click="handleTabClick">
            <el-tab-pane label="订单" name="order"></el-tab-pane>
            <el-tab-pane label="订单明细" name="order-detail"></el-tab-pane>
            <el-tab-pane label="SKU" name="sku"></el-tab-pane>
            <el-tab-pane label="半成品" name="assembly"></el-tab-pane>
          </el-tabs>
          <summary-toolbar v-if="activeToolbar" :key="activeTab" :state="activeToolbar"
            @filter="runToolbarAction('filter', $event)" @refresh="runToolbarAction('refresh')" />
        </nav>
      </header>

      <!-- 各工作台使用独立组件并缓存页面状态 -->
      <main class="workbench-content">
        <keep-alive>
          <ord-suite v-if="activeTab === 'order'" key="tab-order" ref="orderPage" toolbar-in-tabs @toolbar-change="setToolbar('order', $event)" />
          <ord-suite-detail v-else-if="activeTab === 'order-detail'" key="tab-order-detail" ref="orderDetailPage" toolbar-in-tabs @toolbar-change="setToolbar('order-detail', $event)" />
          <sku-work v-else-if="activeTab === 'sku'" key="tab-sku" />
          <assembly-work v-else-if="activeTab === 'assembly'" key="tab-assembly" />
        </keep-alive>
      </main>
    </div>
  </div>
</template>

<script>
import OrdSuite from './ord_suite.vue';
import OrdSuiteDetail from './ord_suite_detail.vue';
import SkuWork from './sku_work.vue';
import AssemblyWork from './assembly_work.vue';

// 页面通过状态与命令接入工具栏，统计和请求仍由各自页面维护。
const SummaryToolbar = {
  name: 'SummaryToolbar',
  props: { state: { type: Object, required: true } },
  render(h) {
    const controls = [];
    if (this.state.counts) {
      const filters = [
        ['all', '全部', 'total'], ['ready', '已齐套', 'ready'], ['partial', '部分齐料', 'partial'],
        ['shortage', '未齐套', 'shortage']
      ];
      filters.forEach(([value, label, count]) => {
        controls.push(h('button', {
          key: value, class: ['readiness-chip', value, { 'is-active': this.state.filter === value }],
          attrs: { type: 'button', disabled: this.state.disabled, 'aria-pressed': this.state.filter === value },
          on: { click: () => this.$emit('filter', value) }
        }, label + ' ' + (this.state.counts[count] != null ? this.state.counts[count] : 0)));
      });
    }
    controls.push(h('el-button', {
      props: { size: 'mini', type: 'text', icon: 'el-icon-refresh', loading: this.state.loading, disabled: this.state.disabled },
      attrs: { title: this.state.snapshotAt ? '数据快照于 ' + this.state.snapshotAt : '刷新', 'aria-label': '刷新' },
      on: { click: () => this.$emit('refresh') }
    }, '刷新'));
    return h('div', { class: 'tab-summary-toolbar', attrs: { role: 'group', 'aria-label': '当前页面齐套统计与刷新' } }, controls);
  }
};

export default {
  name: 'KitMaterialWorkbench',
  components: {
    OrdSuite,
    OrdSuiteDetail,
    SkuWork,
    AssemblyWork,
    SummaryToolbar
  },
  data() {
    return {
      activeTab: 'order',
      toolbarStates: {}
    };
  },
  computed: {
    activeToolbar() {
      return ['order', 'order-detail'].includes(this.activeTab) ? this.toolbarStates[this.activeTab] || null : null;
    }
  },
  methods: {
    setToolbar(key, state) { this.$set(this.toolbarStates, key, state); },
    runToolbarAction(action, value) {
      if (!this.activeToolbar || this.activeToolbar.disabled) return;
      const page = this.$refs[this.activeTab === 'order' ? 'orderPage' : 'orderDetailPage'];
      if (!page) return;
      if (action === 'filter') page.setReadinessFilter(value);
      else if (action === 'refresh') page.loadSuiteData(true);
    },
    handleTabClick(tab) {
      this.activeTab = tab.name;
    }
  }
};
</script>

<style scoped>
.kit-material-workbench {
  --background: #f6f7f9;
  --foreground: #0b1019;
  --card: #ffffff;
  --card-foreground: #0b1019;
  --popover: #ffffff;
  --popover-foreground: #0b1019;
  --primary: #2563eb;
  --primary-foreground: #ffffff;
  --secondary: #f4f4f5;
  --secondary-foreground: #0b1019;
  --muted: #f4f4f5;
  --muted-foreground: #a6abb4;
  --accent: #eff6ff;
  --accent-foreground: #1d4ed8;
  --destructive: #dc2626;
  --destructive-foreground: #ffffff;
  --border: #e4e4e7;
  --input: #d4d4d8;
  --ring: #2563eb;
  --success: #15803d;
  --success-soft: #ecfdf3;
  --warning: #b45309;
  --warning-soft: #fff7ed;
  --shortage-row: #fff1f2;
  --shortage-row-hover: #ffe4e6;
  --radius: 6px;
  --shadow-overlay: 0 18px 48px rgba(24, 24, 27, 0.16);
  --bg: var(--background);
  --surface: var(--card);
  --surface-subtle: #fafafa;
  --line: var(--border);
  --line-strong: var(--input);
  --text: var(--foreground);
  --primary-soft: var(--accent);
  --danger: var(--destructive);
  --danger-soft: var(--shortage-row);
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100% - 34px);
  color: var(--text);
  background: var(--bg);
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif;
  font-size: var(--font-size-body);
  overflow: hidden;
  box-sizing: border-box;
}

.app-shell {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-width: 320px;
  background: var(--bg);
  overflow: hidden;
}

.query-band {
  flex: 0 0 auto;
  min-height: 36px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 0;
  overflow: hidden;
}

.dimension-nav {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0 12px;
  min-height: 36px;
  padding: 0 12px;
  background: var(--surface);
  box-shadow: inset 0 -1px 0 var(--line);
}

.dimension-tabs {
  flex: 0 0 auto;
  width: max-content;
}

.dimension-tabs ::v-deep .el-tabs__header {
  margin: 0;
}
.dimension-tabs ::v-deep .el-tabs__nav-wrap::after {
  display: none;
}
.dimension-tabs ::v-deep .el-tabs__item {
  height: 36px;
  padding: 0 16px;
  color: var(--foreground);
  font-size: 12px;
  line-height: 36px;
  font-weight: 400;
}
.dimension-tabs ::v-deep .el-tabs__item:nth-child(2) {
  padding-left: 0;
}
.dimension-tabs ::v-deep .el-tabs__item:last-child {
  padding-right: 0;
}
.dimension-tabs ::v-deep .el-tabs__item.is-active {
  color: #005bf5;
  font-weight: 600;
}
.dimension-tabs ::v-deep .el-tabs__active-bar {
  height: 2px;
  border-radius: 0;
  background: #005bf5;
}

.workbench-content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  position: relative;
}
.dimension-nav ::v-deep .tab-summary-toolbar {
  display:flex; align-items:center; gap:4px; margin-left:auto; max-width:100%;
  min-height:36px; overflow-x:auto; white-space:nowrap;
}
.dimension-nav ::v-deep .tab-summary-toolbar .readiness-chip {
  flex:none; padding:3px 8px; border:1px solid #cbd5e1; border-radius:4px;
  background:#fff; color:#0b1019; font-size:12px; line-height:20px; cursor:pointer;
}
.dimension-nav ::v-deep .tab-summary-toolbar .readiness-chip.is-active { background:#0f172a; color:#fff; border-color:#0f172a; }
.dimension-nav ::v-deep .tab-summary-toolbar .ready.is-active { background:#16a34a; border-color:#16a34a; }
.dimension-nav ::v-deep .tab-summary-toolbar .partial.is-active { background:#ea580c; border-color:#ea580c; }
.dimension-nav ::v-deep .tab-summary-toolbar .shortage.is-active { background:#dc2626; border-color:#dc2626; }
.dimension-nav ::v-deep .tab-summary-toolbar .readiness-chip:disabled { cursor:default; opacity:.6; }
.dimension-nav ::v-deep .tab-summary-toolbar .el-button { flex:none; margin:0 0 0 6px; color:#0b1019; }
</style>

<style>
/* 保证挂载到 body 下的所有齐套工作台 Dialog / Drawer 拥有完整的 Shadcn 配色、表格布局与交互样式 */
.kit-dialog,
.kit-drawer,
.kit-mat-suite-dialog,
.kit-mat-suite-drawer,
.kit-mat-suite-openai-dialog,
.kit-mat-suite-openai-drawer {
  --background: #f6f7f9;
  --foreground: #0b1019;
  --card: #ffffff;
  --primary: #2563eb;
  --primary-foreground: #ffffff;
  --muted: #f4f4f5;
  --muted-foreground: #a6abb4;
  --accent: #eff6ff;
  --destructive: #dc2626;
  --border: #e4e4e7;
  --input: #d4d4d8;
  --ring: #2563eb;
  --success: #15803d;
  --success-soft: #ecfdf3;
  --warning: #b45309;
  --warning-soft: #fff7ed;
  --shortage-row: #fff1f2;
  --shortage-row-hover: #ffe4e6;
  --radius: 6px;
  --surface: var(--card);
  --surface-subtle: #fafafa;
  --line: var(--border);
  --line-strong: var(--input);
  --text: var(--foreground);
  --primary-soft: var(--accent);
  --danger: var(--destructive);
  --danger-soft: var(--shortage-row);
  --font-size-body: 12px;
  --font-size-hint: 12px;
  --font-size-table-header: 12px;
  --font-weight-table-header: 600;

  color: var(--text);
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif;
  font-size: var(--font-size-body);
  border-radius: var(--radius);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.kit-dialog .el-dialog__header,
.kit-drawer .el-drawer__header,
.kit-mat-suite-dialog .el-dialog__header,
.kit-mat-suite-drawer .el-drawer__header {
  min-height: 48px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--line);
  box-sizing: border-box;
  margin-bottom: 0;
}

.kit-dialog .el-dialog__title,
.kit-drawer .el-drawer__header > :first-child,
.kit-mat-suite-dialog .el-dialog__title,
.kit-mat-suite-drawer .el-drawer__header > :first-child {
  color: var(--foreground);
  font-size: 15px;
  line-height: 24px;
  font-weight: 600;
}

.kit-dialog .el-dialog__headerbtn,
.kit-mat-suite-dialog .el-dialog__headerbtn {
  top: 14px !important;
  right: 16px;
}

.kit-dialog .el-dialog__body,
.kit-drawer .el-drawer__body,
.kit-mat-suite-dialog .el-dialog__body,
.kit-mat-suite-drawer .el-drawer__body {
  padding: 16px;
  color: var(--foreground);
  font-size: var(--font-size-body);
  background: #ffffff;
}

.kit-dialog .el-dialog__footer,
.kit-mat-suite-dialog .el-dialog__footer {
  padding: 10px 16px;
  border-top: 1px solid var(--line);
  background: var(--surface-subtle);
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* 弹窗内的搜索过滤栏 */
.kit-dialog .assembly-catalog-filters,
.kit-mat-suite-dialog .assembly-catalog-filters,
.kit-dialog .sku-catalog-filters,
.kit-mat-suite-dialog .sku-catalog-filters {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

/* 弹窗内的通用元信息与描述 */
.kit-dialog .dialog-meta,
.kit-mat-suite-dialog .dialog-meta {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: var(--surface-subtle);
  border-radius: var(--radius);
  border: 1px solid var(--line);
  font-size: 12px;
}
.kit-dialog .dialog-meta > div,
.kit-mat-suite-dialog .dialog-meta > div {
  display: flex;
  gap: 6px;
}
.kit-dialog .dialog-meta span,
.kit-mat-suite-dialog .dialog-meta span {
  color: var(--muted-foreground);
}
.kit-dialog .dialog-description,
.kit-mat-suite-dialog .dialog-description {
  margin: 0 0 12px 0;
  color: var(--muted-foreground);
  font-size: 12px;
}

/* 弹窗与抽屉内的 Shadcn 风格按钮 */
.kit-dialog .shadcn-button,
.kit-drawer .shadcn-button,
.kit-mat-suite-dialog .shadcn-button,
.kit-mat-suite-drawer .shadcn-button {
  height: 30px;
  padding: 0 12px;
  border-radius: var(--radius);
  font-size: 12px;
  font-weight: 550;
  box-shadow: 0 1px 2px rgba(24, 24, 27, .04);
}

.kit-dialog .shadcn-button--primary,
.kit-drawer .shadcn-button--primary,
.kit-mat-suite-dialog .shadcn-button--primary,
.kit-mat-suite-drawer .shadcn-button--primary {
  color: var(--primary-foreground) !important;
  background: var(--primary) !important;
  border-color: var(--primary) !important;
}
.kit-dialog .shadcn-button--primary:hover,
.kit-mat-suite-dialog .shadcn-button--primary:hover {
  background: #1d4ed8 !important;
  border-color: #1d4ed8 !important;
}

.kit-dialog .shadcn-button--outline,
.kit-drawer .shadcn-button--outline,
.kit-mat-suite-dialog .shadcn-button--outline,
.kit-mat-suite-drawer .shadcn-button--outline {
  color: var(--foreground) !important;
  background: var(--card) !important;
  border-color: var(--input) !important;
}
.kit-dialog .shadcn-button--outline:hover,
.kit-mat-suite-dialog .shadcn-button--outline:hover {
  background: var(--muted) !important;
}

.kit-dialog .shadcn-button--ghost,
.kit-drawer .shadcn-button--ghost,
.kit-mat-suite-dialog .shadcn-button--ghost,
.kit-mat-suite-drawer .shadcn-button--ghost {
  color: var(--muted-foreground) !important;
  background: transparent !important;
  border-color: transparent !important;
  box-shadow: none !important;
}
.kit-dialog .shadcn-button--ghost:hover,
.kit-mat-suite-dialog .shadcn-button--ghost:hover {
  color: var(--foreground) !important;
  background: var(--muted) !important;
}

/* 弹窗与抽屉内的表格样式 */
.kit-dialog .el-table tbody tr,
.kit-drawer .el-table tbody tr,
.kit-mat-suite-dialog .el-table tbody tr,
.kit-mat-suite-drawer .el-table tbody tr {
  height: 32px;
}

.kit-dialog .el-table tbody td,
.kit-drawer .el-table tbody td,
.kit-mat-suite-dialog .el-table tbody td,
.kit-mat-suite-drawer .el-table tbody td {
  height: 31px;
  padding: 0 !important;
}

.kit-dialog .el-table tbody .cell,
.kit-drawer .el-table tbody .cell,
.kit-mat-suite-dialog .el-table tbody .cell,
.kit-mat-suite-drawer .el-table tbody .cell {
  color: var(--foreground);
  font-size: var(--font-size-body);
  height: 31px;
  line-height: 31px;
  padding-left: 8px;
  padding-right: 8px;
  box-sizing: border-box;
}

.kit-dialog .el-table thead th,
.kit-drawer .el-table thead th,
.kit-mat-suite-dialog .el-table thead th,
.kit-mat-suite-drawer .el-table thead th {
  height: 32px;
  padding: 0 !important;
  vertical-align: middle;
  background: #fff !important;
  font-weight: var(--font-weight-table-header);
  font-size: var(--font-size-table-header);
  color: var(--foreground);
}

.kit-dialog .el-table thead th .cell,
.kit-drawer .el-table thead th .cell,
.kit-mat-suite-dialog .el-table thead th .cell,
.kit-mat-suite-drawer .el-table thead th .cell {
  height: 31px;
  padding: 0 8px;
  line-height: 31px;
  white-space: nowrap;
  box-sizing: border-box;
  font-weight: var(--font-weight-table-header);
  color: var(--foreground);
}

.kit-dialog .el-input__inner,
.kit-dialog .el-textarea__inner,
.kit-dialog .el-form-item__label,
.kit-drawer .el-input__inner,
.kit-drawer .el-textarea__inner,
.kit-drawer .el-form-item__label,
.kit-mat-suite-dialog .el-input__inner,
.kit-mat-suite-dialog .el-textarea__inner,
.kit-mat-suite-dialog .el-form-item__label,
.kit-mat-suite-drawer .el-input__inner,
.kit-mat-suite-drawer .el-textarea__inner,
.kit-mat-suite-drawer .el-form-item__label {
  color: var(--foreground);
  font-size: var(--font-size-body);
}

.kit-dialog .el-input__inner::placeholder,
.kit-dialog .el-textarea__inner::placeholder,
.kit-drawer .el-input__inner::placeholder,
.kit-drawer .el-textarea__inner::placeholder,
.kit-mat-suite-dialog .el-input__inner::placeholder,
.kit-mat-suite-dialog .el-textarea__inner::placeholder,
.kit-mat-suite-drawer .el-input__inner::placeholder,
.kit-mat-suite-drawer .el-textarea__inner::placeholder {
  color: var(--muted-foreground);
  font-size: var(--font-size-hint);
  opacity: 1;
}

/* ============================================================ */
/* BOM 悬停浮窗 (Tooltip Popper) 与网格表格样式                  */
/* ============================================================ */
.assembly-sku-bom-tooltip-popper.el-tooltip__popper.is-light,
.sku-bom-tooltip-popper.el-tooltip__popper.is-light {
  --popover: #ffffff;
  --popover-foreground: #0b1019;
  --muted: #f4f4f5;
  --muted-foreground: #71717a;
  --accent: #eff6ff;
  --border: #e4e4e7;
  --input: #d4d4d8;
  --radius: 6px;
  --shadow-overlay: 0 14px 36px rgba(24, 24, 27, 0.14), 0 2px 8px rgba(24, 24, 27, 0.06);

  max-width: calc(100vw - 16px);
  padding: 0;
  color: var(--popover-foreground);
  background: transparent;
  border: 0;
  border-radius: var(--radius);
  box-shadow: none;
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif;
  letter-spacing: 0;
}
.assembly-sku-bom-tooltip-popper.el-tooltip__popper[x-placement^="right"] .popper__arrow,
.sku-bom-tooltip-popper.el-tooltip__popper[x-placement^="right"] .popper__arrow { border-right-color: var(--border); }
.assembly-sku-bom-tooltip-popper.el-tooltip__popper[x-placement^="right"] .popper__arrow::after,
.sku-bom-tooltip-popper.el-tooltip__popper[x-placement^="right"] .popper__arrow::after { border-right-color: var(--popover); }
.assembly-sku-bom-tooltip-popper.el-tooltip__popper[x-placement^="left"] .popper__arrow,
.sku-bom-tooltip-popper.el-tooltip__popper[x-placement^="left"] .popper__arrow { border-left-color: var(--border); }
.assembly-sku-bom-tooltip-popper.el-tooltip__popper[x-placement^="left"] .popper__arrow::after,
.sku-bom-tooltip-popper.el-tooltip__popper[x-placement^="left"] .popper__arrow::after { border-left-color: var(--popover); }
.assembly-sku-bom-tooltip-popper.el-tooltip__popper[x-placement^="top"] .popper__arrow,
.sku-bom-tooltip-popper.el-tooltip__popper[x-placement^="top"] .popper__arrow { border-top-color: var(--border); }
.assembly-sku-bom-tooltip-popper.el-tooltip__popper[x-placement^="top"] .popper__arrow::after,
.sku-bom-tooltip-popper.el-tooltip__popper[x-placement^="top"] .popper__arrow::after { border-top-color: var(--popover); }
.assembly-sku-bom-tooltip-popper.el-tooltip__popper[x-placement^="bottom"] .popper__arrow,
.sku-bom-tooltip-popper.el-tooltip__popper[x-placement^="bottom"] .popper__arrow { border-bottom-color: var(--border); }
.assembly-sku-bom-tooltip-popper.el-tooltip__popper[x-placement^="bottom"] .popper__arrow::after,
.sku-bom-tooltip-popper.el-tooltip__popper[x-placement^="bottom"] .popper__arrow::after { border-bottom-color: var(--popover); }

.assembly-sku-bom-tooltip,
.sku-bom-tooltip {
  width: 420px;
  max-width: calc(100vw - 32px);
  overflow: hidden;
  color: var(--popover-foreground);
  background: var(--popover);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-overlay);
  font-size: 12px;
  box-sizing: border-box;
}
.assembly-sku-bom-tooltip__title,
.sku-bom-tooltip__title {
  min-height: 42px;
  padding: 10px 14px;
  color: var(--popover-foreground);
  background: var(--popover);
  border-bottom: 1px solid var(--border);
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  box-sizing: border-box;
}
.assembly-sku-bom-tooltip__viewport,
.sku-bom-tooltip__viewport {
  max-height: 320px;
  overflow-x: hidden;
  overflow-y: auto;
}
.assembly-sku-bom-tooltip__viewport::-webkit-scrollbar,
.sku-bom-tooltip__viewport::-webkit-scrollbar { width: 8px; }
.assembly-sku-bom-tooltip__viewport::-webkit-scrollbar-track,
.sku-bom-tooltip__viewport::-webkit-scrollbar-track { background: transparent; }
.assembly-sku-bom-tooltip__viewport::-webkit-scrollbar-thumb,
.sku-bom-tooltip__viewport::-webkit-scrollbar-thumb {
  background: var(--input);
  border: 2px solid var(--popover);
  border-radius: 999px;
}
.assembly-sku-bom-tooltip__table,
.sku-bom-tooltip__table { min-width: 0; }
.assembly-sku-bom-tooltip__row,
.sku-bom-tooltip__row {
  display: grid;
  grid-template-columns: 38px 96px minmax(0, 1fr) 52px;
  gap: 8px;
  min-height: 32px;
  padding: 0 12px;
  border-bottom: 1px solid var(--border);
  align-items: center;
  box-sizing: border-box;
}
.assembly-sku-bom-tooltip__row:last-child,
.sku-bom-tooltip__row:last-child { border-bottom: 0; }
.assembly-sku-bom-tooltip__row span,
.sku-bom-tooltip__row span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.assembly-sku-bom-tooltip__row span:first-child,
.sku-bom-tooltip__row span:first-child { color: var(--muted-foreground); text-align: center; }
.assembly-sku-bom-tooltip__row span:nth-child(2),
.sku-bom-tooltip__row span:nth-child(2) { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; font-variant-numeric: tabular-nums; }
.assembly-sku-bom-tooltip__row span:last-child,
.sku-bom-tooltip__row span:last-child { text-align: right; font-variant-numeric: tabular-nums; }
.assembly-sku-bom-tooltip__row:not(.assembly-sku-bom-tooltip__row--head):hover,
.sku-bom-tooltip__row:not(.sku-bom-tooltip__row--head):hover { background: var(--accent); }
.assembly-sku-bom-tooltip__row--head,
.sku-bom-tooltip__row--head {
  position: sticky;
  top: 0;
  min-height: 34px;
  color: var(--muted-foreground);
  background: var(--muted);
  box-shadow: inset 0 -1px 0 var(--border);
  font-weight: 600;
}
.assembly-sku-bom-tooltip__empty,
.sku-bom-tooltip__empty { padding: 22px 14px; color: var(--muted-foreground); text-align: center; }
</style>
